import { createClient } from "@workos-inc/authkit-js";

export type AuthUser = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
};

let client: Awaited<ReturnType<typeof createClient>> | null = null;
let initPromise: Promise<void> | null = null;

function clientId(): string {
  const id = import.meta.env.VITE_WORKOS_CLIENT_ID;
  if (!id) throw new Error("VITE_WORKOS_CLIENT_ID is not set");
  return id;
}

export async function initAuth(): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    try {
      client = await createClient(clientId());
      await client.initialize();
    } catch (err) {
      client = null;
      initPromise = null;
      throw err;
    }
  })();
  return initPromise;
}

function requireClient() {
  if (!client) throw new Error("Auth not initialized — call initAuth() first");
  return client;
}

export function currentUser(): AuthUser | null {
  const u = requireClient().getUser();
  if (!u) return null;
  return {
    id: u.id,
    email: u.email,
    firstName: u.firstName ?? null,
    lastName: u.lastName ?? null,
  };
}

export async function signIn(): Promise<void> {
  await requireClient().signIn();
}

export async function signOut(): Promise<void> {
  await requireClient().signOut();
}

export async function getAccessToken(): Promise<string | null> {
  const c = requireClient() as unknown as { getAccessToken?: () => Promise<string | null> };
  if (typeof c.getAccessToken !== "function") return null;
  return c.getAccessToken();
}
